import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

const SOCIAL_MEDIA_PLATFORM_OPTIONS = [
  { value: 'wetransfer', label: 'WeTransfer' },
  { value: 'dropbox', label: 'Dropbox' },
  { value: 'google_drive', label: 'Google Drive' },
  { value: 'custom', label: 'Custom...' },
];

interface SocialMediaLinksManagerProps {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
}

export const SocialMediaLinksManager = ({ value, onChange }: SocialMediaLinksManagerProps) => {
  const [newPlatform, setNewPlatform] = useState('');
  const [customPlatform, setCustomPlatform] = useState('');
  const [newUrl, setNewUrl] = useState('');

  const entries = Object.entries(value || {});

  const handleAdd = () => {
    const platform = newPlatform === 'custom' ? customPlatform.trim().toLowerCase().replace(/\s+/g, '_') : newPlatform;
    if (!platform || !newUrl) return;

    onChange({
      ...value,
      [platform]: newUrl,
    });
    setNewPlatform('');
    setCustomPlatform('');
    setNewUrl('');
  };

  const handleRemove = (platform: string) => {
    const newValue = { ...value };
    delete newValue[platform];
    onChange(newValue);
  };

  const handleUpdateUrl = (platform: string, url: string) => {
    onChange({
      ...value,
      [platform]: url,
    });
  };

  const getPlatformLabel = (key: string) => {
    const found = SOCIAL_MEDIA_PLATFORM_OPTIONS.find(p => p.value === key);
    if (found) return found.label;
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-3">
      {entries.map(([platform, url]) => (
        <div key={platform} className="flex gap-2 items-center">
          <div className="w-32 text-sm font-medium text-muted-foreground">
            {getPlatformLabel(platform)}
          </div>
          <Input
            value={url}
            onChange={(e) => handleUpdateUrl(platform, e.target.value)}
            placeholder="https://..."
            className="flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemove(platform)}
            className="h-9 w-9 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <div className="flex gap-2 items-end pt-2 border-t">
        <div className="space-y-1">
          <Select value={newPlatform} onValueChange={setNewPlatform}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              {SOCIAL_MEDIA_PLATFORM_OPTIONS.filter(p => !value?.[p.value] || p.value === 'custom').map((platform) => (
                <SelectItem key={platform.value} value={platform.value}>
                  {platform.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {newPlatform === 'custom' && (
          <Input
            value={customPlatform}
            onChange={(e) => setCustomPlatform(e.target.value)}
            placeholder="Platform name"
            className="w-32"
          />
        )}

        <Input
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="https://..."
          className="flex-1"
        />

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleAdd}
          disabled={!newPlatform || (newPlatform === 'custom' && !customPlatform) || !newUrl}
          className="h-9 w-9"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
